/**
 * Hook for managing run music history
 */

import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { useToast } from './use-toast';

interface Track {
  name: string;
  artist: string;
  bpm?: number;
  energy?: number;
  genre?: string;
  spotify_uri?: string;
}

interface MusicHistoryEntry {
  track: Track;
  playedAt: Date;
  userBPM: number;
  syncQuality: 'perfect' | 'good' | 'adjusting' | 'poor';
}

export function useRunMusicHistory() {
  const { toast } = useToast();
  const [history, setHistory] = useState<MusicHistoryEntry[]>([]);
  const runIdRef = useRef<string | null>(null);

  const startSession = useCallback((runId: string) => {
    runIdRef.current = runId;
    setHistory([]);
    logger.info('Started music history session', { runId });
  }, []);

  const trackPlayed = useCallback((track: Track, userBPM: number, isSynced: boolean, isAdjusting: boolean) => {
    if (!runIdRef.current) return;

    // Determine sync quality
    let syncQuality: 'perfect' | 'good' | 'adjusting' | 'poor' = 'poor';
    if (isAdjusting) {
      syncQuality = 'adjusting';
    } else if (track.bpm && userBPM > 0) {
      const diff = Math.abs(userBPM - track.bpm);
      if (diff <= 5) syncQuality = 'perfect';
      else if (diff <= 10) syncQuality = 'good';
      else if (diff <= 15) syncQuality = 'adjusting';
    }

    const entry: MusicHistoryEntry = {
      track,
      playedAt: new Date(),
      userBPM,
      syncQuality
    };

    setHistory(prev => [...prev, entry]);
    
    logger.info('Track played', {
      track: track.name,
      userBPM,
      trackBPM: track.bpm,
      syncQuality
    });
  }, []);

  const saveHistory = useCallback(async () => {
    if (!runIdRef.current || history.length === 0) return;

    try {
      const records = history.map(entry => ({
        run_id: runIdRef.current,
        track_name: entry.track.name,
        artist: entry.track.artist,
        bpm: entry.track.bpm || 0,
        energy: entry.track.energy,
        genre: entry.track.genre,
        played_at: entry.playedAt.toISOString(),
        spotify_uri: entry.track.spotify_uri,
        user_bpm: entry.userBPM,
        sync_quality: entry.syncQuality
      }));

      const { error } = await supabase
        .from('run_music_history')
        .insert(records);

      if (error) throw error;

      logger.info('Music history saved', {
        runId: runIdRef.current,
        trackCount: records.length
      });

      return true;
    } catch (error) {
      logger.error('Failed to save music history', error, {
        runId: runIdRef.current
      });
      return false;
    }
  }, [history]);

  const getStatistics = useCallback(() => {
    if (history.length === 0) return null;

    const totalTracks = history.length;
    const avgUserBPM = Math.round(
      history.reduce((sum, entry) => sum + entry.userBPM, 0) / totalTracks
    );
    
    const syncQualities = {
      perfect: history.filter(e => e.syncQuality === 'perfect').length,
      good: history.filter(e => e.syncQuality === 'good').length,
      adjusting: history.filter(e => e.syncQuality === 'adjusting').length,
      poor: history.filter(e => e.syncQuality === 'poor').length
    };

    const syncRate = Math.round(
      ((syncQualities.perfect + syncQualities.good) / totalTracks) * 100
    );

    const genres = [...new Set(history.map(e => e.track.genre).filter(Boolean))];
    
    const avgEnergy = history.filter(e => e.track.energy).length > 0
      ? Math.round(
          history.reduce((sum, e) => sum + (e.track.energy || 0), 0) / 
          history.filter(e => e.track.energy).length
        )
      : null;

    return {
      totalTracks,
      avgUserBPM,
      syncRate,
      syncQualities,
      genres,
      avgEnergy,
      mostPlayedGenre: genres.length > 0 ? genres[0] : null
    };
  }, [history]);

  return {
    history,
    startSession,
    trackPlayed,
    saveHistory,
    getStatistics
  };
}
